import { FormEvent, useEffect, useMemo, useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Sheet,
  SheetBody,
  SheetContent,
  SheetDescription,
  SheetFooter,
  SheetHeader,
  SheetTitle,
} from "@/components/ui/sheet";
import { createDraftTemplate, updateDraftTemplate } from "@/lib/api/drafts";
import { pageDescriptionMobileHiddenClassName } from "@/lib/page-action-button";
import { DraftTemplateSectionList } from "./DraftTemplateSectionList";
import {
  buildTemplateStructure,
  parseTemplateStructure,
  sectionsForSave,
  templateSectionsToEditorRows,
  type TemplateSectionEditorRow,
} from "./draft-template-sections";

export interface DraftTemplateSheetDraft {
  mode: "create" | "edit" | "customize";
  id?: string;
  name: string;
  value: string;
  blurb: string;
  version: string;
  structure: unknown;
}

interface DraftTemplateSheetProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  draft: DraftTemplateSheetDraft | null;
  onSaved?: () => void;
}

export function DraftTemplateSheet({
  open,
  onOpenChange,
  draft,
  onSaved,
}: DraftTemplateSheetProps) {
  const [name, setName] = useState("");
  const [value, setValue] = useState("");
  const [blurb, setBlurb] = useState("");
  const [version, setVersion] = useState("v1.0");
  const [sectionRows, setSectionRows] = useState<TemplateSectionEditorRow[]>(() =>
    templateSectionsToEditorRows([])
  );
  const [isSaving, setIsSaving] = useState(false);

  const title = useMemo(() => {
    if (!draft) return "Template";
    if (draft.mode === "create") return "New template";
    if (draft.mode === "edit") return "Edit template";
    return "Customize template";
  }, [draft]);

  useEffect(() => {
    if (!open || !draft) return;
    setName(draft.name);
    setValue(draft.value);
    setBlurb(draft.blurb);
    setVersion(draft.version);
    setSectionRows(templateSectionsToEditorRows(parseTemplateStructure(draft.structure)));
  }, [open, draft]);

  async function handleSubmit(event: FormEvent) {
    event.preventDefault();
    if (!draft || !name.trim() || !value.trim()) return;

    const structure = buildTemplateStructure(sectionsForSave(sectionRows));

    setIsSaving(true);
    try {
      if (draft.mode === "edit" && draft.id) {
        await updateDraftTemplate(draft.id, {
          name: name.trim(),
          value: value.trim(),
          blurb: blurb.trim(),
          version: version.trim() || "v1.0",
          structure,
        });
      } else {
        await createDraftTemplate({
          name: name.trim(),
          value: value.trim(),
          blurb: blurb.trim() || undefined,
          version: version.trim() || "v1.0",
          structure,
        });
      }
      onSaved?.();
      onOpenChange(false);
    } catch (reason) {
      console.error(reason);
    } finally {
      setIsSaving(false);
    }
  }

  const lockValue = draft?.mode === "customize";

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent side="right" className="p-0 sm:max-w-lg">
        <form onSubmit={handleSubmit} className="flex h-full flex-col">
          <SheetHeader>
            <SheetTitle>{title}</SheetTitle>
            <SheetDescription className={pageDescriptionMobileHiddenClassName}>
              Templates define section structure used when generating a new draft.
            </SheetDescription>
          </SheetHeader>

          <SheetBody className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="dt-name">Display name</Label>
              <Input
                id="dt-name"
                value={name}
                onChange={(e) => setName(e.target.value)}
                required
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="dt-value">Value key</Label>
              <Input
                id="dt-value"
                value={value}
                onChange={(e) => setValue(e.target.value)}
                disabled={lockValue}
                required
              />
              {lockValue ? (
                <p className="text-xs text-[var(--lm-muted)] dark:text-slate-500">
                  Value key stays aligned with the system template so new drafts resolve the correct
                  structure.
                </p>
              ) : null}
            </div>

            <div className="grid gap-4 sm:grid-cols-2">
              <div className="space-y-2">
                <Label htmlFor="dt-version">Version label</Label>
                <Input
                  id="dt-version"
                  value={version}
                  onChange={(e) => setVersion(e.target.value)}
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="dt-blurb">Short description</Label>
              <Textarea
                id="dt-blurb"
                rows={3}
                value={blurb}
                onChange={(e) => setBlurb(e.target.value)}
                placeholder="Shown in the template picker and export context"
              />
            </div>

            <DraftTemplateSectionList
              rows={sectionRows}
              onChange={setSectionRows}
              disabled={isSaving}
            />
          </SheetBody>

          <SheetFooter>
            <Button type="button" variant="surface" onClick={() => onOpenChange(false)}>
              Cancel
            </Button>
            <Button type="submit" disabled={isSaving}>
              {isSaving ? "Saving…" : "Save"}
            </Button>
          </SheetFooter>
        </form>
      </SheetContent>
    </Sheet>
  );
}

export function draftTemplateToSheetDraft(
  item: {
    id: string;
    name: string;
    value: string;
    blurb: string | null;
    version: string;
    structure: unknown;
  },
  mode: "edit" | "customize"
): DraftTemplateSheetDraft {
  return {
    mode,
    id: mode === "edit" ? item.id : undefined,
    name: item.name,
    value: item.value,
    blurb: item.blurb ?? "",
    version: item.version,
    structure: item.structure ?? { sections: [] },
  };
}
